import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Sensor, UserAccessRole, UserGroup, standardHttpOptions } from '@models';
import { ActionQueue } from '@models/ActionQueue/ActionQueue.model';
import { DeviceInfo } from '@models/Device/DeviceInfo';
import { GlobalSearchCriteria, GlobalSearchHitInfo, GlobalSearchType, SearchResult, SearchResultGroup, SearchResultLink, SearchResultOwner } from '@models/GlobalSearch/globalSearch.model';
import { Observable, concat, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Authorization } from 'app/shared/classes/authorization';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { ActionQueueService } from './actionQueue.service';
import { DeviceService } from './device.service';
import { SensorService } from './sensor.service';
import { UserService } from './user.service';

export interface GlobalSearchOutcome {
  groups: SearchResultGroup[];
  total: number;
  // True when the server part of the search failed, so the results may be incomplete
  incomplete: boolean;
  // True while the server part of the search is still running: these are the results found in the
  // browser so far, and the server's are merged in when they arrive.
  pending: boolean;
}

interface Candidate {
  field: string;
  value: string | null | undefined;
  // What to show in the snippet when this candidate matched, if not the matched value itself
  display?: string;
}

// How each result type is shown, in the order the groups appear.
const GROUPS: { type: GlobalSearchType; label: string; pageName: string }[] = [
  { type: GlobalSearchType.Dashboard, label: 'Dashboards', pageName: 'Dashboards' },
  { type: GlobalSearchType.Sensor, label: 'Sensors', pageName: 'Sensors' },
  { type: GlobalSearchType.SensorGroup, label: 'Sensor Groups', pageName: 'Sensor Groups' },
  { type: GlobalSearchType.Gateway, label: 'Gateways', pageName: 'Gateways' },
  { type: GlobalSearchType.Event, label: 'Events & Actions', pageName: 'Events & Actions' },
  { type: GlobalSearchType.ActionQueue, label: 'Action Queues', pageName: 'Action Queues' },
  { type: GlobalSearchType.ChartFavorite, label: 'Chart Favorites', pageName: 'Analyze Sensor Readings' },
  { type: GlobalSearchType.MapFavorite, label: 'Map Favorites', pageName: 'GeoMap Readings' },
  { type: GlobalSearchType.ImageFavorite, label: 'Image Favorites', pageName: 'Scoutview' },
  { type: GlobalSearchType.Import, label: 'Imports & Syncs', pageName: 'Imports & Syncs' },
  { type: GlobalSearchType.UserGroup, label: 'User Groups', pageName: 'User Groups' }
];

const MAX_PER_TYPE_FROM_SERVER = 50;

// Finds items across the app for the home page search box.
//
// Item types the UI has already loaded are searched right here, from the services' cached lists, and
// reported straight away. Everything else is found with one POST /globalsearch, whose results are
// merged in when they arrive. The server also owns every type that is found through a relationship
// (sensor groups, events, chart and map favorites, dashboards), because the browser does not hold those
// relationships. Each type is searched in exactly one place, so the two sets of results never overlap.
//
// It only reads the caches. It never starts a load, so typing never raises the loading overlay.
@Injectable()
export class GlobalSearchService {
  private apiUrl: string;
  private auth = new Authorization();

  constructor(private http: HttpClient,
              @Inject(KIG_SETTINGS) private settings: KigSettings,
              private sensorService: SensorService,
              private deviceService: DeviceService,
              private actionQueueService: ActionQueueService,
              private userService: UserService) {
    this.apiUrl = this.settings.apiUrl;
  }


  public static readonly MIN_TEXT_LENGTH = 2;


  // Emits the matches found in the browser at once (pending), then, when the server answers, all of them.
  public search(rawText: string, username: string, userGroups: UserGroup[], globalSwitch: boolean): Observable<GlobalSearchOutcome> {
    const text = (rawText ?? '').trim().toLowerCase();
    if (text.length < GlobalSearchService.MIN_TEXT_LENGTH) {
      return of({ groups: [], total: 0, incomplete: false, pending: false });
    }

    const local: SearchResult[] = [];
    const serverTypes: string[] = [];

    // Sensors and gateways find each other (a sensor by its gateway, a gateway by its sensors), so the
    // browser can only answer for them when it has both lists.
    const sensors = this.cachedSensors();
    const gateways = this.deviceService.devices;
    if (sensors != null && gateways?.length > 0) {
      local.push(...this.searchSensors(text, sensors, gateways), ...this.searchGateways(text, gateways, sensors));
    } else {
      serverTypes.push(GlobalSearchType.Sensor, GlobalSearchType.Gateway);
    }

    const queues = this.actionQueueService.actionQueues;
    queues?.length > 0 ? local.push(...this.searchActionQueues(text, queues)) : serverTypes.push(GlobalSearchType.ActionQueue);

    // Always on the server: found through relationships the browser does not hold, or not cached at all.
    serverTypes.push(GlobalSearchType.SensorGroup, GlobalSearchType.Event, GlobalSearchType.Dashboard,
                     GlobalSearchType.ChartFavorite, GlobalSearchType.MapFavorite, GlobalSearchType.ImageFavorite,
                     GlobalSearchType.Import);

    local.push(...this.searchUserGroups(text, userGroups ?? []));

    const accessible = (r: SearchResult) => this.canView(r, username, userGroups ?? [], globalSwitch);
    const localAllowed = local.filter(accessible);

    const criteria: GlobalSearchCriteria = { searchText: rawText.trim(), types: serverTypes, maxPerType: MAX_PER_TYPE_FROM_SERVER };

    const fromBrowser$ = of(this.group(localAllowed, false, true));

    const fromServer$ = this.http.post<GlobalSearchHitInfo[]>(`${ this.apiUrl }/globalsearch`, JSON.stringify(criteria), standardHttpOptions).pipe(
      map(hits => ({ hits: hits ?? [], failed: false })),
      catchError(() => of({ hits: [] as GlobalSearchHitInfo[], failed: true })),
      map(({ hits, failed }) => {
        const fromServer = hits
          .map(h => this.resultFromServerHit(h, text))
          .filter((r): r is SearchResult => r != null)
          .filter(accessible);
        return this.group([...localAllowed, ...fromServer], failed, false);
      })
    );

    return concat(fromBrowser$, fromServer$);
  }


  // ---- Cached lists. Read directly; the get...() methods would start a load. ----

  private cachedSensors(): Sensor[] | null {
    if (this.sensorService.allSensorsWithCalibrations?.length > 0) return this.sensorService.allSensorsWithCalibrations;
    if (this.sensorService.allSensorsWithoutTypes?.length > 0) return this.sensorService.allSensorsWithoutTypes;
    return null;
  }


  // ---- Searching the cached lists ----

  private searchSensors(text: string, sensors: Sensor[], gateways: DeviceInfo[]): SearchResult[] {
    const gatewaysBySensor = new Map<string, DeviceInfo[]>();
    for (const gateway of gateways) {
      for (const uuid of gateway.sensorUUIDs ?? []) {
        const key = uuid?.toLowerCase();
        if (key == undefined) continue;
        const list = gatewaysBySensor.get(key);
        list ? list.push(gateway) : gatewaysBySensor.set(key, [gateway]);
      }
    }

    return this.collect(sensors, text, GlobalSearchType.Sensor, s => ({
      id: s.id,
      title: s.name || s.uuid,
      subtitle: s.uuid,
      owner: { userName: s.userName, userGroupId: s.userGroupId },
      candidates: [
        { field: 'Name', value: s.name }, { field: 'UUID', value: s.uuid }, { field: 'Type', value: s.type },
        { field: 'Reference', value: s.referenceInfo }, { field: 'Notes', value: s.notes }
      ],
      // A sensor is also found by the gateway it reports through
      related: () => (gatewaysBySensor.get(s.uuid?.toLowerCase()) ?? []).flatMap(g => {
        const display = this.label(g.name, g.serialNumber);
        return [
          { field: 'Gateway', value: g.name, display }, { field: 'Gateway', value: g.serialNumber, display },
          { field: 'Gateway', value: g.macAddress, display }
        ];
      })
    }));
  }


  private searchGateways(text: string, gateways: DeviceInfo[], sensors: Sensor[]): SearchResult[] {
    const sensorsByUuid = new Map<string, Sensor>();
    for (const sensor of sensors) {
      if (sensor.uuid != undefined) sensorsByUuid.set(sensor.uuid.toLowerCase(), sensor);
    }

    return this.collect(gateways, text, GlobalSearchType.Gateway, d => ({
      id: d.id,
      title: d.name || d.serialNumber,
      subtitle: d.serialNumber,
      owner: { userName: d.userName, userGroupId: d.userGroupId },
      candidates: [
        { field: 'Name', value: d.name }, { field: 'Description', value: d.description },
        { field: 'Serial number', value: d.serialNumber }, { field: 'MAC address', value: d.macAddress }
      ],
      // A gateway is also found by the sensors that report through it
      related: () => (d.sensorUUIDs ?? []).flatMap(uuid => {
        const sensor = sensorsByUuid.get(uuid?.toLowerCase());
        if (sensor == undefined) return [];
        const display = this.label(sensor.name, sensor.uuid);
        return [{ field: 'Sensor', value: sensor.name, display }, { field: 'Sensor', value: sensor.uuid, display }];
      })
    }));
  }


  private searchActionQueues(text: string, queues: ActionQueue[]): SearchResult[] {
    return this.collect(queues, text, GlobalSearchType.ActionQueue, q => ({
      id: q.id,
      title: q.name,
      owner: { userName: q.userName, userGroupId: q.userGroupId },
      candidates: [{ field: 'Name', value: q.name }]
    }));
  }


  // User groups have no owner: who may see one depends on the user's role in it (or being an admin).
  private searchUserGroups(text: string, userGroups: UserGroup[]): SearchResult[] {
    return this.collect(userGroups, text, GlobalSearchType.UserGroup, g => ({
      id: g.id,
      title: g.name,
      owner: {},
      candidates: [{ field: 'Name', value: g.name }, { field: 'Description', value: g.description }]
    }));
  }


  // Shared by every local search: keep the items where a field contains the text, and describe the first
  // field that did (name first) for the snippet. An item that matches on none of its own fields can still
  // match through what it is related to.
  private collect<T>(items: T[], text: string, type: GlobalSearchType,
                     describe: (item: T) => { id: number; title: string; subtitle?: string;
                                              owner: SearchResultOwner; candidates: Candidate[];
                                              related?: () => Candidate[] }): SearchResult[] {
    const matches = (c: Candidate) => c.value != null && c.value.toLowerCase().includes(text);
    const results: SearchResult[] = [];

    for (const item of items ?? []) {
      const d = describe(item);
      const matched = d.candidates.find(matches) ?? d.related?.().find(matches);
      if (matched == undefined) continue;

      results.push(this.makeResult(type, d.id, d.title, d.subtitle, matched.field, matched.display ?? (matched.value as string),
                                   text, d.owner, item));
    }
    return results;
  }


  private resultFromServerHit(hit: GlobalSearchHitInfo, text: string): SearchResult | null {
    const type = hit.type as GlobalSearchType;
    if (!GROUPS.some(g => g.type === type)) return null;

    return this.makeResult(type, hit.id, hit.title, hit.subtitle, hit.matchedField, hit.matchedText, text,
                           { userName: hit.userName, userGroupId: hit.userGroupId }, hit);
  }


  private makeResult(type: GlobalSearchType, id: number, title: string, subtitle: string | undefined,
                     matchedField: string, matchedText: string, text: string, owner: SearchResultOwner, source: any): SearchResult {
    const safeTitle = title ?? '';
    const lowerTitle = safeTitle.toLowerCase();
    const rank = lowerTitle.startsWith(text) ? 0 : lowerTitle.includes(text) ? 1 : 2;
    const [link, extraLinks] = this.linksFor(type, id, safeTitle, source);

    return { type, id, title: safeTitle, subtitle: subtitle || undefined, matchedField, matchedText, rank, link, extraLinks, owner };
  }


  // "Name (identifier)", or whichever of the two there is
  private label(name: string | null | undefined, identifier: string | null | undefined): string {
    if (!name) return identifier ?? '';
    return identifier ? `${ name } (${ identifier })` : name;
  }


  // The page each result opens, and the query param that page reads to show the matched item.
  private linksFor(type: GlobalSearchType, id: number, title: string, source: any): [SearchResultLink, SearchResultLink[]] {
    switch (type) {
      case GlobalSearchType.Dashboard:
        return [{ label: 'Open dashboard', route: `/dashboard/${ id }` }, []];

      case GlobalSearchType.Sensor:
        return [
          { label: 'Open in Sensors', route: '/sensors', queryParams: { search: source.uuid ?? source.subtitle ?? title } },
          [{ label: 'Analyze readings', route: '/sensorreadings', queryParams: { sensorId: id } }]
        ];

      case GlobalSearchType.SensorGroup:
        return [{ label: 'Open in Sensor Groups', route: '/sensorgroups', queryParams: { search: title } }, []];

      case GlobalSearchType.Gateway:
        return [{ label: 'Open in Gateways', route: '/register', queryParams: { search: source.serialNumber ?? source.subtitle ?? title } }, []];

      case GlobalSearchType.Event:
        return [{ label: 'Open in Events & Actions', route: '/events', queryParams: { search: title } }, []];

      case GlobalSearchType.ActionQueue:
        return [{ label: 'Open in Action Queues', route: '/actionqueues', queryParams: { search: title } }, []];

      case GlobalSearchType.ChartFavorite:
        return [{ label: 'Open chart', route: '/sensorreadings', queryParams: { chartFavoriteId: id } }, []];

      case GlobalSearchType.MapFavorite:
        return [{ label: 'Open map', route: '/mapreadings', queryParams: { mapFavoriteId: id } }, []];

      case GlobalSearchType.ImageFavorite:
        return [{ label: 'Open images', route: '/scoutview', queryParams: { imageFavoriteId: id } }, []];

      case GlobalSearchType.Import:
        return [{ label: 'Open in Imports & Syncs', route: '/import', queryParams: { search: title } }, []];

      default:
        return [{ label: 'Open in User Groups', route: '/usergroups', queryParams: { search: title } }, []];
    }
  }


  // The same rule every page uses for ViewListing: any role in the item's group (or ownership, or being
  // an admin) shows the item; so does the Stage environment switch.
  private canView(result: SearchResult, username: string, userGroups: UserGroup[], globalSwitch: boolean): boolean {
    if (globalSwitch) return true;
    if (username == undefined || username === '') return false;

    if (result.type === GlobalSearchType.UserGroup) {
      const group = userGroups.find(g => g.id === result.id);
      const adminGroup = userGroups.find(g => g.name?.toLowerCase() === 'admins') ?? ({ userInfos: [] } as unknown as UserGroup);
      return group != undefined && this.auth.getUserRoleForUserGroup(group, username, adminGroup) !== UserAccessRole.Unassigned;
    }

    // A sensor nobody owns is visible to everyone (as on the Sensors page).
    if (result.type === GlobalSearchType.Sensor && result.owner.userName == undefined) return true;

    const [role] = this.auth.getUserRole(result.owner.userGroupId ?? null, result.owner.userName, username, userGroups);
    return role !== UserAccessRole.Unassigned;
  }


  private group(results: SearchResult[], incomplete: boolean, pending: boolean): GlobalSearchOutcome {
    const groups: SearchResultGroup[] = [];
    for (const g of GROUPS) {
      const inGroup = results
        .filter(r => r.type === g.type)
        .sort((a, b) => a.rank - b.rank || a.title.localeCompare(b.title, 'en', { numeric: true }));
      if (inGroup.length > 0) {
        groups.push({ type: g.type, label: g.label, pageName: g.pageName, results: inGroup });
      }
    }
    return { groups, total: results.length, incomplete, pending };
  }
}
